import { useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import { ComputerDesktopIcon, DevicePhoneMobileIcon } from '@heroicons/react/20/solid';
import { motion } from 'framer-motion';

import { ChromeColoredIcon, ChromeIcon } from '@/components/icons/ElementIcons';
import WalletCard from '@/components/shared/kp/WalletCard';
import BgEllipse from '@/components/shared/lp/BgEllipse';

import KomodoWalletDownloads from '@/data/KomodoWalletDownloads.json';
import { siteData } from '@/data/configs/site';
import laptopMobile1 from '@/public/images/lp/downloads/laptop-mobile-1.png';
import android from '@/public/images/lp/komodo-wallet/android.svg';
import ios from '@/public/images/lp/komodo-wallet/ios.svg';
import linux from '@/public/images/lp/komodo-wallet/linux.svg';
import macos from '@/public/images/lp/komodo-wallet/macos.svg';
import windows from '@/public/images/lp/komodo-wallet/windows.svg';
import androidqr from '@/public/images/lp/mobile/android-qr.png';
import iosqr from '@/public/images/lp/mobile/ios-qr.png';
import gradientLinkArrow from '@/public/images/lp/svgs/gradient-link-arrow.svg';

export default function Wallets() {
  const [selectedFilter, setSelectedFilter] = useState('All');

  const filteredWalletList = (filter) => {
    if (filter === 'All') return walletListData;
    return walletListData.filter((wallet) => wallet.platforms.includes(filter));
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <>
      <main className='container relative z-[100] mx-auto font-sans text-white md:grid md:grid-cols-24 md:gap-0'>
        {/* <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className='lp-ellipse-223 absolute left-[30%] top-[-200px] z-[-10] h-[359px] w-[287px] -translate-x-1/2 lg:left-[40%] -sm:top-[-200px]'
        ></motion.div> */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className='lp-ellipse-224 absolute left-[-350px] top-[163px] z-[-10] h-[515px] w-[492px] xl:left-[-300px] -sm:left-[-400px] -sm:top-[400px]'
        ></motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className='lp-ellipse-232 absolute right-[-500px] top-[800px] z-[-10] h-[733px] w-[700px] -sm:right-[-300px]'
        ></motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className='lp-ellipse-233 absolute left-[-600px] top-[1700px] z-[-10] h-[783px] w-[749px]'
        ></motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className='lp-ellipse-234 absolute right-[-700px] top-[2700px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className='lp-ellipse-239 absolute bottom-[-500px] left-[-300px] z-[-10] h-[910px] w-[875px]'
        ></motion.div>
        <div className='hidden xl:col-span-2 xl:block'></div>
        <div className='relative w-full max-w-7xl px-4 xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className='mx-auto flex flex-col items-center px-5 pt-[80px] sm:pt-[100px] lg:pt-[100px]'
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className='home-small-header font-sans text-xs font-normal sm:text-sm xl:text-base 2xl:text-lg'
            >
              Featured
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className='mb-[60px] mt-1 flex items-center text-center font-heading text-[73px] font-semibold leading-[120%] tracking-[-2.19px] text-white hover:text-gray-300 sm:mb-[50px] lg:block -sm:text-[40px]'
            >
              Komodo Wallet
            </motion.h1>
            <div className='rounded-[14px] border border-[#303030]'>
              <Link href={siteData.downloadsPage}>
                <ExportedImage
                  placeholder='empty'
                  alt='laptop-mobile'
                  className='mx-auto h-auto w-full lg:h-[55vh] lg:w-auto'
                  src={laptopMobile1}
                  priority
                />
              </Link>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 17,
                delay: 0.3,
                duration: 0.6,
              }}
            >
              <Link
                href={siteData.downloadsPage}
                className='mt-4 rounded-[14px] bg-gradient-to-r from-[#289fc6] to-[#5a68e6] px-6 py-3 text-base font-medium text-white transition-all duration-200 hover:from-[#2ab0db] hover:to-[#6574f0] active:from-[#2497b8] active:to-[#4f5dcc] md:text-lg'
              >
                Download
              </Link>
            </motion.div>
          </motion.section>
          <section className='section py-md-12 py-9 pt-[80px] lg:pt-[120px] 2xl:pt-[200px]'>
            <BgEllipse className='-left-32 -z-10' />
            <div className='mx-auto'>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className='fold-head pb-[2rem] text-center'
              >
                <h2 className='mb-[1rem] text-[2.25rem]'>Browse All Wallets</h2>
                <p className='mt-[1rem]'>List of available wallets that support KMD.</p>
              </motion.div>
              <div className='row'>
                <div className='col-md-12'>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className='app-lists alignl filter-menu'
                  >
                    <nav className='flex flex-wrap items-center justify-center gap-5'>
                      {['All', 'Desktop', 'Mobile', 'Hardware', 'Paper Wallet', 'Web'].map(
                        (item, index) => (
                          <motion.button
                            key={item}
                            onClick={() => setSelectedFilter(item)}
                            className={`${
                              selectedFilter !== item
                                ? 'border-[#303030] bg-[#061621] hover:border-[#404040] hover:bg-[#0a1f2d] active:bg-[#0d2736]'
                                : 'border border-transparent bg-gradient-to-r from-[#289fc6] to-[#5a68e6] hover:from-[#2ab0db] hover:to-[#6574f0] active:from-[#2497b8] active:to-[#4f5dcc]'
                            } cursor-pointer rounded-[14px] border p-2 px-4 transition-all duration-200 ease-in-out`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 0.3,
                              delay: 0.1 * index,
                              type: 'spring',
                              stiffness: 400,
                              damping: 17,
                            }}
                          >
                            <span className='font-medium'>{item}</span>
                          </motion.button>
                        ),
                      )}
                    </nav>
                  </motion.div>
                  <motion.div
                    variants={container}
                    initial='hidden'
                    whileInView='show'
                    viewport={{ once: true }}
                    className='mx-auto mt-10 max-w-[1200px]'
                  >
                    <div
                      className='grid grid-cols-1 gap-6 px-5 pt-4 sm:grid-cols-2 lg:grid-cols-3'
                      id='appfilter'
                      data-isotope='{"layoutMode": "fitRows"}'
                    >
                      {filteredWalletList(selectedFilter).map((item, index) => (
                        <motion.div key={index} variants={item}>
                          <WalletCard
                            altText={item.altText}
                            link={item.link}
                            width={item.width}
                            height={item.height}
                            image={item.image}
                            affiliation={item.affiliation}
                            name={item.name}
                            platforms={item.platforms}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </section>
        </div>
        <div className='hidden xl:col-span-2 xl:block'></div>
      </main>
    </>
  );
}

const walletListData = [
  {
    altText: 'Komodo Wallet',
    link: siteData.downloadsPage,
    width: '260',
    height: '70',
    image: '/images/kp/wallets/komodo-wallet-white.png',
    affiliation: 'Official',
    name: 'Komodo Wallet',
    platforms: ['Mobile', 'Desktop', 'Web'],
  },
  {
    altText: 'Ocean QT Wallet',
    link: 'https://github.com/DeckerSU/KomodoOcean/releases',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/ocean-qt-light.png',
    affiliation: 'Official',
    name: 'Ocean QT Wallet',
    platforms: ['Desktop'],
  },
  {
    altText: 'Komodo CLI',
    link: 'https://github.com/KomodoPlatform/komodo/releases',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/komodo-cli-light.png',
    affiliation: 'Official',
    name: 'Komodo CLI',
    platforms: ['Desktop'],
  },
  {
    altText: 'Komodo Paper Wallet',
    link: 'https://deckersu.github.io/coinbin/#home',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/paper-wallet-light.png',
    affiliation: 'Official',
    name: 'Komodo Paper Wallet',
    platforms: ['Paper Wallet'],
  },
  {
    altText: 'Verus Wallet',
    link: 'https://verus.io/wallet',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/verus-wallet.png',
    affiliation: '3rd Party',
    name: 'Verus Wallet',
    platforms: ['Mobile', 'Desktop'],
  },
  {
    altText: 'Guarda Wallet',
    link: 'https://guarda.com/',
    width: '47',
    height: '61',
    image: '/images/kp/wallets/guarda-wallet.svg',
    affiliation: '3rd Party',
    name: 'Guarda Wallet',
    platforms: ['Mobile', 'Desktop', 'Web'],
  },
  {
    altText: 'Ledger',
    link: 'https://www.ledger.com/',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/ledger-light.png',
    affiliation: '3rd Party',
    name: 'Ledger',
    platforms: ['Hardware'],
  },
  {
    altText: 'Trezor',
    link: 'https://trezor.io/',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/trezor-light.png',
    affiliation: '3rd Party',
    name: 'Trezor',
    platforms: ['Hardware'],
  },
  {
    altText: 'Coinomi',
    link: 'https://coinomi.com/',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/coinomi-light.png',
    affiliation: '3rd Party',
    name: 'Coinomi',
    platforms: ['Mobile', 'Desktop'],
  },
  {
    altText: 'ZelCore',
    link: 'https://zel.network/',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/zelCore-light.png',
    affiliation: '3rd Party',
    name: 'ZelCore',
    platforms: ['Mobile', 'Desktop'],
  },
  {
    altText: 'Kriptomat',
    link: 'https://kriptomat.io/',
    width: '260',
    height: '70',
    image: '/images/kp/wallets/kriptomat.png',
    affiliation: '3rd Party',
    name: 'Kriptomat',
    platforms: ['Mobile', 'Web'],
  },
];
